import React from 'react';
import { Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Card, Typography, Button } from '../../primitives';

/**
 * DataBuilderCard
 * Standardized 3-step wizard card used for the Data Builder flow.
 *
 * This is intentionally UI-only for now – no form wiring or backend calls.
 */
const DataBuilderCard = ({
  currentStep = 1,
  totalSteps = 3,
  title,
  onNext,
  onBack,
  isFirstStep,
  isLastStep,
  canProceed = true,
  hideNavigation = false,
  children,
  footer,
}) => {
  const theme = useTheme();

  const activeStepColor = theme.palette.primary.main;
  const completedStepColor = theme.palette.secondary.main;
  const pendingColor = theme.palette.divider;

  const renderStepCircle = (stepNumber) => {
    const isActive = stepNumber === currentStep;
    const isCompleted = stepNumber < currentStep;

    const backgroundColor = isActive
      ? activeStepColor
      : isCompleted
      ? completedStepColor
      : pendingColor;

    return (
      <Box
        key={stepNumber}
        sx={{
          width: 28,
          height: 28,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: theme.palette.primary.contrastText,
          fontWeight: 700,
          fontSize: 14,
          backgroundColor,
        }}
      >
        {stepNumber}
      </Box>
    );
  };

  const renderConnector = (stepNumber) => {
    const isCompleted = stepNumber < currentStep;
    const backgroundColor = isCompleted ? completedStepColor : pendingColor;

    return (
      <Box
        key={`connector-${stepNumber}`}
        sx={{
          width: 40,
          height: 3,
          borderRadius: 999,
          backgroundColor,
        }}
      />
    );
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: theme.palette.background.default,
        padding: 1,
      }}
    >
      <Card
        elevation={2}
        sx={{
          width: '100%',
          maxWidth: { xs: '100%', md: '800px' },
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          backgroundColor: theme.palette.background.paper,
          border: `1px solid ${theme.palette.divider}`,
          paddingY: 4,
          paddingX: 5,
        }}
      >
        {/* Stepper row */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1.5,
          }}
        >
          {Array.from({ length: totalSteps }).map((_, index) => {
            const stepNumber = index + 1;
            return (
              <React.Fragment key={stepNumber}>
                {renderStepCircle(stepNumber)}
                {stepNumber < totalSteps && renderConnector(stepNumber)}
              </React.Fragment>
            );
          })}
        </Box>

        {/* Title */}
        <Box
          sx={{
            mt: 5,
            textAlign: 'center',
          }}
        >
          <Typography
            variant="h5"
            component="h1"
            sx={{
              fontWeight: 700,
            }}
          >
            {title}
          </Typography>
        </Box>

        {/* Content area for step-specific UI (placeholders for now) */}
        <Box
          sx={{
            flex: 1,
            mt: 4,
          }}
        >
          {children}
        </Box>

        {/* Navigation buttons */}
        {!hideNavigation && (
          <Box
            sx={{
              mt: 6,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            {/* Back button (hidden on first step) */}
            <Box>
              {!isFirstStep && (
                <Button
                  variant="outlined"
                  onClick={onBack}
                >
                  ← Back
                </Button>
              )}
            </Box>

            {/* Next / Done button */}
            <Box>
              <Button
                variant="contained"
                onClick={onNext}
                disabled={!canProceed}
              >
                {isLastStep ? 'Done' : 'Next →'}
              </Button>
            </Box>
          </Box>
        )}

        {footer && (
          <Box sx={{ mt: 4 }}>
            {footer}
          </Box>
        )}
      </Card>
    </Box>
  );
};

export default DataBuilderCard;

